import { Component, ReactNode, RefObject } from 'react';
import * as React from 'react';

interface IProps {
	id?: string;
	className?: string;
	placeholder?: string;
	name?: string;
	value?: string;
	autoFocus?: boolean;
	disabled?: boolean;

	children?: ReactNode;

	// Important to add this number as calculations are based on this number.
	// This should potentially be made dynamic in order to support responsive designs.
	lineHeight: number;
	minRows: number;
	maxRows: number;
	onChange?: (event: React.FormEvent<HTMLTextAreaElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
}

interface IState {
	rows: number;
}

export class InputResizable extends Component<IProps, IState> {
	inputRef: RefObject<HTMLTextAreaElement> = React.createRef();

	constructor(props: IProps) {
		super(props);

		const { minRows } = props;

		this.state = {
			rows: minRows,
		};
	}

	componentDidMount() {
		const { autoFocus } = this.props;

		if (this.inputRef != null && this.inputRef.current) {
			const element = this.inputRef.current;
			this.adjustHeight(element);

			if (autoFocus === true) {
				element.focus();
			}
		}
	}

	handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		const { onChange } = this.props;

		this.adjustHeight(event.target);

		if (typeof onChange === 'function') {
			onChange(event);
		}
	};

	handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
		const { onBlur } = this.props;

		if (typeof onBlur === 'function') {
			onBlur(event);
		}
	};

	adjustHeight = (element: HTMLTextAreaElement): void => {
		const { minRows, maxRows, lineHeight } = this.props;

		const previousRows = element.rows;
		element.rows = minRows; // reset number of rows in textarea

		const style = getComputedStyle(element);
		const paddingTop = parseInt(style.paddingTop.split('px')[0]);
		const paddingBottom = parseInt(style.paddingBottom.split('px')[0]);
		const currentRows = Math.round(
			(element.scrollHeight - paddingTop - paddingBottom) / lineHeight,
		);

		if (currentRows === previousRows) {
			element.rows = currentRows;
		}

		if (currentRows >= maxRows) {
			element.rows = maxRows;
			element.scrollTop = element.scrollHeight;
		}

		this.setState({
			rows: currentRows < maxRows ? currentRows : maxRows,
		});
	};

	render() {
		const { id, children, className, name, placeholder, value, disabled } = this.props;
		const { rows } = this.state;

		// eslint-disable-next-line react/no-children-prop
		return React.createElement('textarea', {
			id,
			children,
			className,
			style: {
				lineHeight: `${this.props.lineHeight}px`,
			},
			name,
			rows,
			value,
			placeholder,
			disabled,
			ref: (ref: RefObject<HTMLTextAreaElement>) => {
				this.inputRef = ref;
			},
			onChange: this.handleChange,
			onBlur: this.handleBlur,
		});
	}
}
